import React from 'react';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';

export type ApprovalStatusFilterValue = 'pending' | 'approved' | 'rejected' | 'all';

export interface ApprovalStatusFilterOption {
  value: ApprovalStatusFilterValue;
  label: string;
  count: number;
}

interface ApprovalStatusFilterProps {
  value: ApprovalStatusFilterValue;
  options: ApprovalStatusFilterOption[];
  onChange: (value: ApprovalStatusFilterValue) => void;
  ariaLabel: string;
}

/**
 * Segmented control para filtrar filas de aprovação (pendente / aprovado / rejeitado).
 * Os counts vêm do pai para refletir o escopo real da lista.
 */
const ApprovalStatusFilter: React.FC<ApprovalStatusFilterProps> = ({
  value,
  options,
  onChange,
  ariaLabel,
}) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_event, next: ApprovalStatusFilterValue | null) => {
      if (next !== null) onChange(next);
    }}
    aria-label={ariaLabel}
    sx={{ flexWrap: 'wrap' }}
  >
    {options.map((opt) => (
      <ToggleButton key={opt.value} value={opt.value} aria-label={opt.label}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{opt.label}</span>
          <Chip
            label={opt.count}
            size="small"
            color={value === opt.value ? 'primary' : 'default'}
          />
        </Stack>
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

export default ApprovalStatusFilter;

/** Correção de hora e banco: pending | approved | rejected. */
export function matchesSimpleApprovalStatus(
  status: string,
  filter: ApprovalStatusFilterValue,
): boolean {
  if (filter === 'all') return true;
  return status === filter;
}

/** Justificativa: pendente = enviado + em análise. */
export function matchesJustificationStatus(
  status: string,
  filter: ApprovalStatusFilterValue,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'pending') return status === 'sent' || status === 'under_review';
  return status === filter;
}

/** Nota fiscal: pendente = pending + under_review. */
export function matchesInvoiceStatus(
  status: string,
  filter: ApprovalStatusFilterValue,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'pending') return status === 'pending' || status === 'under_review';
  return status === filter;
}

export function countByApprovalFilter<T extends { status: string }>(
  items: T[],
  matcher: (status: string, filter: ApprovalStatusFilterValue) => boolean,
): Record<ApprovalStatusFilterValue, number> {
  return {
    pending: items.filter((i) => matcher(i.status, 'pending')).length,
    approved: items.filter((i) => matcher(i.status, 'approved')).length,
    rejected: items.filter((i) => matcher(i.status, 'rejected')).length,
    all: items.length,
  };
}

export function buildApprovalFilterOptions(
  counts: Record<ApprovalStatusFilterValue, number>,
  labels?: Partial<Record<ApprovalStatusFilterValue, string>>,
): ApprovalStatusFilterOption[] {
  return [
    { value: 'pending', label: labels?.pending ?? 'Pendentes', count: counts.pending },
    { value: 'approved', label: labels?.approved ?? 'Aprovadas', count: counts.approved },
    { value: 'rejected', label: labels?.rejected ?? 'Rejeitadas', count: counts.rejected },
    { value: 'all', label: labels?.all ?? 'Todas', count: counts.all },
  ];
}
